import { ApplicationEnum } from "../../enums/ApplicationEnum";
import { IAddress } from "../vue-interfaces/i-address";
import { IDiagnosis, IProblems } from "../vue-interfaces/i-diagnosis";
import {
    IPalDemographics,
    IPalPayerInformation,
    IPalClinicalInformation,
    IPalHealthHistory,
    IPalProviders,
    IPalPharmacyAndDme,
    IPalAuthorizedContacts,
    IPalEmergencyPreparedness,
    IPalAdvanceDirectives,
    IPalPatientGoals,
    IPalReferralInformation,
    IPalliativeConvertToPending,
    IPalMyNotes,
    ICreatePatientNoteRequest
} from "../vue-interfaces/i-palliative-convert-to-pending";
import { IPharmacy } from "../vue-interfaces/i-pharmacy";
import { IEvacuationPhoneNumber, IPhoneNumber } from "../vue-interfaces/i-phone-number";
import { Address } from "./address";
import { AuthorizedContact } from "./authorized-contact";
import { PhoneNumber } from "./phone-number";

export class PalliativeConvertToPending implements IPalliativeConvertToPending {
    public demographics: IPalDemographics = new PalDemographics();
    public payerInformation: IPalPayerInformation = new PalPayerInformation();
    public clinicalInformation: IPalClinicalInformation = new PalClinicalInformation();
    public healthHistory: IPalHealthHistory = new PalHealthHistory();
    public providers: IPalProviders = new PalProviders();
    public pharmacyAndDme: IPalPharmacyAndDme = new PalPharmacyAndDme();
    public authorizedContacts: IPalAuthorizedContacts = new AuthorizedContact();
    public emergencyPreparedness: IPalEmergencyPreparedness = new PalEmergencyPreparedness();
    public advanceDirectives: IPalAdvanceDirectives = new PalAdvanceDirectives();
    public patientGoals: IPalPatientGoals = new PalPatientGoals();
    public referralInformation: IPalReferralInformation = new PalReferralInformation();
    public patientNotes: ICreatePatientNoteRequest = new PatientNoteData();
    public myNotes: IPalMyNotes = new PalMyNotes();

    public constructor(data?: PalliativeConvertToPending) {
        if (data) {
            Object.assign(this, data);
        }
    }
}

export class PalDemographics {
    public locationId: string;
    public firstName: string;
    public lastName: string;
    public suffix: string;
    public middleInitial: string;
    public middleName: string;
    public gender: number;
    public genderIdentity: number;
    public otherGenderIdentity: string;
    public sexualOrientation: number;
    public otherSexualOrientation: string;
    public dateOfBirth: string;
    public medicalRecordNumber: string;
    public socialSecurityNumber: string;
    public maritalStatus: number;
    public admittedFromLocation: number;
    public primaryAddress: IAddress = new Address();
    public isVisitAddressSameAsPrimary: boolean;
    public visitAddress: IAddress = new Address();
    public isMailingAddressSameAsPrimary: boolean;
    public mailingAddress: IAddress = new Address();
    public previousAddress: IAddress = new Address();
    public phoneNumbers: IPhoneNumber[] = [new PhoneNumber()];
    public email: string;
    public declineToSpecifyLanguage: boolean;
    public primaryLanguage: string;
    public languages: string[];
    public interpreter: number[];
    public otherInterpreter: string;
    public auxiliaryAids: string;
    public veteran: number[];
    public race: number[];
    public ethnicity: number[];
    public ethnicityOnly: number;
    public declineToSpecifyEthnicity: boolean;
    public declineToSpecifyRace: boolean;
    public faceSheetComments: string;
    public primaryAddressFacilityTypeId: string;
    public visitAddressFacilityTypeId: string;
    public mailingAddressFacilityTypeId: string;
    public previousAddressFacilityTypeId: string;
    public tagIds: string[];
    public tags: [];
    public cahpsSurveyContactType: number;
    public hasPreviousAddress: boolean;
}

export class PalPayerInformation {
    public currentReferralId: string = "";
    public id: string = "";
    public medicareNumber: string = "";
    public medicareNumberNA: string = "";
    public medicareBeneficiaryIdentification: string = "";
    public medicareBeneficiaryIdentificationNA: boolean = false;
    public medicaidNumber: string = "";
    public notAMedicaid: boolean = false;
    public medicaidPending: boolean = false;
    public paymentSource: number[] = null;
}

export class PalClinicalInformation {
    public caseManagerId: string;
    public clinicalManagerId: string;
    public tolerablePainLevel: number;
    public admittingNurseId: string;
    public communityLiaisonId: string;
    public aideId: string;
    public socialWorkerId: string;
    public spiritualCounselorId: string;
    public referringDiagnosis: IDiagnosis;
    public diagnoses: IDiagnosis[];
    public problems: IProblems[];
    public careTeamId: string;
}

export class PalHealthHistory {
    public patientClinicalHistories: {
        id?: string;
        type: number;
        description: string;
    }[];
    public pastMedicalHistories: [
        {
            id: string;
            snapshotId: string;
            description: string;
            code: string;
            associatedTaskId: string;
            billable: boolean;
            startDate: string;
            resolvedDate: string;
            resolvedOrder: number;
            isRelated: boolean;
            notRelatedComments: string;
            isDeprecated: boolean;
            isStartDateUnknown: boolean;
            isResolvedDateUnknown: boolean;
        }
    ];
    public resolvedProblems: [
        {
            code: string;
            description: string;
            startDate: string;
            resolvedDate: string;
            resolvedOrder: number;
            order: number;
            isPresentOnAdmission: boolean;
            id: string;
            snapshotId: string;
            isStartDateUnknown: boolean;
            isResolvedDateUnknown: boolean;
        }
    ];
    public pastMedicalHistoryComment: string;
    public showPediatricHistory: boolean = false;
    public pediatricHistory: {
        tbTest: boolean;
        leadScreening: boolean;
        otherPediatricScreening: string;
        gestationalAgeAtBirth: number | string;
        lengthScale: number;
        lengthAtBirth: number;
        newBornScreeningResult: string;
        birthWeightScale: number;
        birthWeight: number;
        newBornHistory: boolean;
        headCircumferenceScale: number;
        headCircumference: number;
        fontanels: number[];
        umbilicus: number[];
    };
    public smokingStatus: number;
    public smokingStartDate: string;
    public smokingEndDate: string;
    public historicalSmokingStatus: number;
    public historicalSmokingStartDate: string;
    public historicalSmokingEndDate: string;
}

export class PalProviders {
    public primaryCareProviderId: string;
    public palliativeProviderId: string;
    public referringProviderId: string;
    public additionalProviderIds: any[];
}

export class PalPharmacyAndDme {
    public associatedTaskId: string;
    public dmEs: [
        {
            id: string;
            agencyDMEId: string;
            associatedTaskId: string;
            startDate: string;
            endDate: string;
            notRelatedComments: string;
        }
    ];
    public supplies: [
        {
            id: string;
            supplyId: string;
            quantity: number;
            associatedTaskId: string;
            startDate: string;
            endDate: string;
            notRelatedComments: string;
        }
    ];
    public pharmacies: string[];
    public supplyVendors: string[];
    public dmeVendors: string[];
    public isAttachOrderNotRequired: boolean;
}

export class PalAuthorizedContacts {
    public patientId: string;
    public firstName: string;
    public lastName: string;
    public email: string;
    public dateOfBirth: string;
    public race: number[];
    public gender: number;
    public ethnicity: number;
    public isPrimary: boolean;
    public isUsingPrimaryAddress: boolean;
    public address: IAddress = new Address();
    public isLegalRepresentative: boolean;
    public isPatientSelectedRepresentative: boolean;
    public relationship: number;
    public otherRelationshipDescription: string;
    public phoneNumbers: IPhoneNumber = new PhoneNumber();
    public contactRoles: number[];
    public isEmergencyContact: boolean;
    public isPrimaryBereaved: boolean;
    public contactOrder: number;
    public hasFamilyPortalAccess: boolean;
    public isBereavedContact: boolean;
    public isPrimaryEmergencyContact: boolean;
    public declineToSpecifyRace: boolean;
    public declineToSpecifyEthnicity: boolean;
}

export class PalEmergencyPreparedness {
    public emergencyPreparednessInformationValues: number[];
    public id: string;
    public emergencyPreparednessPriority: number;
    public additionalEmergencyInformation: string;
    public evacuationZone: number;
    public isEvacuationAddressSameAsEmergencyContact: boolean;
    public emergencyContactAddress: IAddress;
    public evacuationAddress: IAddress;
    public evacuationPrimaryPhoneNumber: IEvacuationPhoneNumber;
    public evacuationAlternatePhoneNumber: IEvacuationPhoneNumber;
}

export class PalAdvanceDirectives {
    public advancedDirectives: {
        hasLegalDocumentation: boolean;
        isCarePlanDocumented: boolean;
        isSurrogateDocumented: boolean;
        advancedCarePlanDirectives: number[];
        codeStatus: number[];
        other: string;
        attorneyName: string;
        attorneyRelationship: string;
        medicalPreferences: string;
        mentalPreferences: string;
        culturalPreferences: string;
        spiritualPreferences: string;
        surrogateDecisionMaker: string;
        dateAdvancedDirectiveIsMarkedYes: string;
    };
    public patientFuneralHome: PatientFuneralHome;
}

export class PatientFuneralHome {
    public id: string = "";
    public name: string = "";
    public address: string = "";
    public city: string = "";
    public state: string = "";
    public zipCode: string = "";
    public phoneNumber: string = "";
    public afterHrsPhoneNumber: string = "";

    public constructor(data?: PalliativeConvertToPending) {
        if (data) {
            Object.assign(this, data);
        }
    }
}

export class PalPatientGoals {
    public personalGoals: string;
    public careGoals: string;
    public barriersToPersonalOrCareGoals: string;
    public crisisCarePlan: string;
}

export class PalReferralInformation {
    public isReferral: boolean;
    public referralDate: string;
    public referralReason: string;
    public patientCurrentLocation: string;
    public comments: string;
    public communityLiaison: string;
    public communityLiaisonId: string;
    public facilityReferralSourceId: string;
    public facilityReferralSourceName: string;
    public facilityReferralSourceContactName: string;
    public physicianReferralSourceId: string;
    public physicianReferralSourceName: string;
    public physicianReferralSourceContactName: string;
    public otherReferralSourceRelationship: number;
    public otherReferralSourceName: string;
    public internalReferralSourceId: string;
    public internalReferralSource: number;
    public internalReferralSourceName: string;
    public sourceOfReferral: number;
    public pendingReason: number;
    public pendingReasonComment: string;
    public referringProviderName: string;
    public referringProviderId: string;
    public referringFacilityId: string;
    public referringFacilityName: string;
    public inquiryDate: string;
}

export class PatientNoteData implements ICreatePatientNoteRequest {
    public note: string = "";
}

export class PalMyNotes implements IPalMyNotes {
    public note: string = "";
}
